import { useEffect } from 'react';

import { isCustomerFeatureEnabled } from 'src/data/customer';
import {
  INCOMING_MESSAGE,
  messagePlatformReady,
  messagePlatformInfo,
  messageDisplayFullscreen,
} from 'src/data/infinity';
import { APPLICATION_MODE, getApplicationHost, getApplicationMode } from 'src/data/system';
import { sendMessageToParent, subscribeToMessages } from 'src/lib/messages';
import { BE_CUSTOMER_FEATURES } from 'src/lib/featureFlags';
import { createStateSelectorHook } from 'src/lib/sweetState';
import { trackUserEventNoAuth, prefixExtensionEventName, getCdNamesValues } from 'src/lib/events';
import { ROUTES } from 'src/routes';
import {
  useSystemActions,
  useUserActions,
  useCustomerActions,
  useWorkspaceActions,
  UiStore,
} from 'src/store';
import { useGetCommonEventAuthAttributes, useGetSystemEventAttributes } from 'src/services';
import { mapUserForInfinity, mapCustomerForInfinity, mapWorkspaceForInfinity } from './utils';

/**
 * If on initial App load we have relevant param in URL - switch app mode
 */
const useSetupSystemStoreData = () => {
  const systemActions = useSystemActions();

  useEffect(() => {
    const applicationMode = getApplicationMode();
    if (applicationMode === APPLICATION_MODE.infinityExtension) {
      systemActions.activateApplicationMode(APPLICATION_MODE.infinityExtension);
    }

    const applicationHost = getApplicationHost();
    if (applicationHost) {
      systemActions.setApplicationHost(applicationHost);
    }
  }, [systemActions]);
};

const useSetupExtensionCommunication = () => {
  const userActions = useUserActions();
  const customerActions = useCustomerActions();
  const workspaceActions = useWorkspaceActions();
  const getCommonEventAuthAttributes = useGetCommonEventAuthAttributes();
  const getSystemEventAttributes = useGetSystemEventAttributes();

  // Set up base communication with extension
  useEffect(() => {
    /**
     * Handle incoming "check info" message from Extension
     */
    const processPlatformInfoCheckMessage = () => {
      const user = userActions.getCurrentUser();
      const customer = customerActions.getCurrentCustomer();
      const workspace = workspaceActions.getCurrentWorkspace();

      const authenticated = Boolean(user);
      const canUseExtension = isCustomerFeatureEnabled(
        customer,
        BE_CUSTOMER_FEATURES.infinityExtension
      );

      const message = messagePlatformInfo({
        authenticated,
        canUseExtension,
        user: user ? mapUserForInfinity(user) : null,
        customer: customer ? mapCustomerForInfinity(customer) : null,
        workspace: workspace ? mapWorkspaceForInfinity(workspace) : null,
      });
      sendMessageToParent(message);
    };

    /**
     * Logout user from Platform
     */
    const processLogoutMessage = () => {
      userActions.logout(true);
      window.location.assign(ROUTES.SIGN_IN);
    };

    /**
     * Track event from Extension
     * @param {object} content - event data
     */
    const processTrackEventMessage = (content) => {
      const name = prefixExtensionEventName(content.name);
      const attributes = {
        ...getCommonEventAuthAttributes(),
        ...getSystemEventAttributes(),
        ...getCdNamesValues(content.attributes),
      };
      trackUserEventNoAuth(name, attributes);
    };

    // Subscribe to parent messages
    const unsubscribe = subscribeToMessages((message) => {
      const { type, content } = message.data;

      // eslint-disable-next-line default-case
      switch (type) {
        case INCOMING_MESSAGE.platformInfoCheck: {
          processPlatformInfoCheckMessage();
          break;
        }

        case INCOMING_MESSAGE.logout: {
          processLogoutMessage();
          break;
        }

        case INCOMING_MESSAGE.trackEvent: {
          processTrackEventMessage(content);
          break;
        }
      }
    });

    // Notify Extension that Platform is ready for messaging
    const message = messagePlatformReady();
    sendMessageToParent(message);

    return () => {
      unsubscribe();
    };
  }, [
    userActions,
    customerActions,
    workspaceActions,
    getCommonEventAuthAttributes,
    getSystemEventAttributes,
  ]);
};

/**
 * Notify Extension that Platform displays fullscreen popup
 */
const useUiState = createStateSelectorHook(UiStore, [
  'globalPopupVisibleCount',
  'localPopupVisibleCount',
]);
export const useReactToFullscreenPopups = () => {
  const { globalPopupVisibleCount, localPopupVisibleCount } = useUiState();

  useEffect(() => {
    const fullscreenPopupVisible = globalPopupVisibleCount > 0 || localPopupVisibleCount > 0;
    const message = messageDisplayFullscreen(fullscreenPopupVisible);
    sendMessageToParent(message);
  }, [globalPopupVisibleCount, localPopupVisibleCount]);
};

export const useSupportInfinityExtension = () => {
  useSetupSystemStoreData();
  useSetupExtensionCommunication();
  useReactToFullscreenPopups();
};
